import { formatCollection } from "./formatCollection";
import {
  FILE_SIZE_PREVIEW_LIMIT_BYTES,
  FILE_SIZE_PREVIEW_TOO_LARGE_MESSAGE,
} from "./fileSizeConstants";
import { notifications } from "@mantine/notifications";
import { sha3_256_hash_bytes } from "./cryptoUtils";
import DOMPurify from "dompurify";

export const URLs = Object.freeze({
  PADDLE_DETAILS_URL: "/paddle/",
  PADDLE_REFRESH_CREDITS_URL: "/paddle/refresh-credits/",
  PADDLE_TRANSACTION_STATUS_URL: "/paddle/transaction-status/",
  CONFIG_URL: "/profile/config/",
  PLANS_URL: "/paddle/billing-subscription-plan/",
  REPORTS_PAGE_URL: "/reports/",
  REPORTS_URL: "/reports/data/?page=",
  REPORTS_PRODUCER_URL: "/reports/producer/data/?page=",
  USER_INFO_URL: "/users/user-info/",
  USER_INFO_USER_ADDRESS_HISTORY_URL: "/users/user-address-history/",
  USER_PROFILE_DELETE_ACCOUNT_URL: "/profile/delete-account/",
  PROFILE_API_KEYS_URL: "/profile/get-user-data/",
  USER_INFO_SAVE_PROFILE_URL: "/profile/update-user-profile-data/",
  PROFILE_STATISTICS_URL: "/profile/update-user-data/",
  PROFILE_PAYMENT_URL: "/profile/payment/",
  PROFILE_COLLECTIONS_URL: "/profile/collections/",
  ACCOUNTS_SIGNUP_URL: "/accounts/signup/",
  ACCOUNTS_LOGIN_URL: "/accounts/login/",
  CHECK_AUTHENTICATION_URL: "/profile/check-authentication/",
  VERIFY_URL: "/verify/",
  STAMP_URL: "/stamp/",
  PADDLE_CANCEL_URL: "/paddle/cancel/",
  CONTACTS_URL: "https://www.vbase.com/contact/",
  CONTACT_APP_URL: "/contact/",
  SUBSCRIPTION_SUCCESS_URL: "/paddle/subscription-success/",
  SUBSCRIPTION_FAILED_URL: "/paddle/subscription-failed/",
  USER_FOUND_DATA_URL: "/verify/find-user-objects/",
  USER_COLLECTIONS_URL: "/profile/#collections",
  USER_BILLING_URL: "/profile/#billing",
  VERUFY_API_URL: "/verify/cid/",
  VERIFY_USER_DATA_URL: "/verify/user-data/",
  DOWNLOAD_VERIFIED_FILE: "/storage/download-verified-file/",
  PREVIEW_VERIFIED_FILE: "/storage/preview-verified-file/",
  VERIFIED_FILE_HASH: "/verify/link-id/",

  CREATE_VERIFIED_FILE_DOWNLOAD: "/storage/create-verified-file/",

  INTERACTIVE_BROKERS_AUTHORIZATION_ACCOUNTS_URL:
    "/auth/interactive-brokers/accounts",

  PROVIDERS_URL: "/auth/get_user_brokerage_providers",

  NOTIFICATIONS_URL: "/notifications",

  // Collection verify (CSV upload on Verify page)
  VERIFY_COLLECTION_URL: "/collections/verify/",

  // S3 collection validation (link check, credentials POST, async status)
  S3_LINK_VALIDATION_URL: "/verify/s3-link/",
  S3_CREDENTIALS_VALIDATION_URL: "/collections/verify/s3/",
  S3_VALIDATION_STATUS_URL: "/collections/verify/s3-status/",
});

// Check user data type
function prepareRequestData(queryString) {
  const user = queryString.toLowerCase();
  if (user.startsWith("0x")) {
    return { user_address: user };
  } else if (user.startsWith("vbase")) {
    return { persistent_id: user };
  } else {
    return { user_name: user };
  }
}

export const userFoundData = async (
  setInputValues,
  user = "",
  setLoading,
  userAddress,
  persistentId,
  userName,
  collectionName = "",
  collectionCid = "",
) => {
  let requestData = {};
  if (user) {
    requestData = prepareRequestData(user);
  }
  if (userAddress) {
    requestData.user_address = userAddress;
  } else if (persistentId) {
    requestData.persistent_id = persistentId;
  } else if (userName) {
    requestData.user_name = userName;
  }

  if (collectionName) {
    requestData.collection_name = collectionName;
  }
  if (collectionCid) {
    requestData.collection_cid = collectionCid;
  }

  try {
    setLoading(true);

    const csrfToken = document.querySelector(
      "[name=csrfmiddlewaretoken]",
    ).value;

    const response = await fetch(URLs.USER_FOUND_DATA_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRFToken": csrfToken,
      },
      body: JSON.stringify(requestData),
    });
    if (!response.ok) {
      // Check for specific status 400
      if (response.status === 400) {
        setInputValues({
          user_logged_in: false,
        });
        setLoading(false); // Stop loading if response status is 400
        return; // Exit the function to prevent further execution
      } else {
        throw new Error(
          `Network response was not ok. Status: ${response.status}`,
        );
      }
    }
    const data = await response.json();

    if (data) {
      const filteredReceipts = (data?.commitment_receipts_data || []).sort(
        (a, b) => {
          return new Date(b.timestamp) - new Date(a.timestamp);
        },
      );

      setInputValues({
        user_found: true,
        user_logged_in: true,
        collection_data: data?.collection_data || [],
        commitment_receipts_data: filteredReceipts,
        user_info_data: data?.user_info_data || {},
      });
    } else {
      setInputValues({
        user_found: false,
        user_logged_in: true,
      });
    }
    setLoading(false);
  } catch (error) {
    setInputValues({
      user_found: false,
      user_logged_in: true,
    });
  }
  setLoading(false);
};

export const userFoundDataForCollectionFound = async (
  setTotalCollections,
  setFirstStampInCollection,
  setLastStampInCollection,
  setTotalStampInCollection,
  userAddress,
  collectionHash,
  setUserVerifiedInfo,
) => {
  if (!userAddress) {
    console.warn("No user address provided");
    return;
  }

  if (!collectionHash) {
    console.warn(
      "No collection_id in verify response; find-user-objects request is unscoped and per-collection stats cannot be selected.",
    );
  }

  const requestData = {
    user_address: userAddress,
  };
  if (collectionHash) {
    requestData.collection_cid = collectionHash;
  }

  const csrfInput = document.querySelector("[name=csrfmiddlewaretoken]");
  if (!csrfInput) {
    console.error("CSRF token not found in DOM");
    return;
  }

  const csrfToken = csrfInput.value;

  try {
    const response = await fetch(URLs.USER_FOUND_DATA_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRFToken": csrfToken,
      },
      body: JSON.stringify(requestData),
    });

    if (!response.ok) {
      if (response.status === 400) {
        console.warn("Bad request (400). Skipping data update.");
        return;
      }
      throw new Error(`Request failed with status ${response.status}`);
    }

    const data = await response.json();

    if (data) {
      setTotalCollections(data.user_info_data.total_collections || "");

      if (setUserVerifiedInfo) {
        setUserVerifiedInfo({
          is_verified: data.user_info_data?.is_verified ?? false,
          verified_since: data.user_info_data?.verified_since ?? null,
        });
      }

      const collectionsData = data.collection_data || [];

      const selectedCollection = collectionsData?.find(
        (item) => item.collection_hash === collectionHash,
      );

      if (selectedCollection) {
        const { count, first_timestamp, last_timestamp } = selectedCollection;
        setFirstStampInCollection(first_timestamp);
        setLastStampInCollection(last_timestamp);
        setTotalStampInCollection(count);
      } else if (collectionHash) {
        console.warn(
          "Collection with specified hash not found in find-user-objects response.",
        );
      }
    }
  } catch (error) {
    console.error("Error fetching user data:", error);
  }
};

export const paddleDataSuccess = async (setInputValues, setIsLoading) => {
  try {
    const response = await fetch(URLs.PADDLE_DETAILS_URL);
    if (!response.ok) {
      throw new Error("Network response was not ok");
    }
    const dataList = await response.json();
    const data = dataList[0];
    setInputValues({
      subscription_info: {
        subscription_paddle_id:
          data.subscription_info?.subscription_paddle_id || false,
        last_transaction_paddle_id:
          data.subscription_info.last_transaction_paddle_id || false,
      },
      user_balance_info: {
        expirable_credits: data.user_balance_info.expirable_credits || 0,
        paid_credits: data.user_balance_info.paid_credits || 0,
        total_credits: data.user_balance_info.total_credits || 0,
      },
      subscription_plan: {
        title: data.subscription_plan?.title || false,
        total_quota_limit: data.subscription_plan?.total_quota_limit || 0,
      },
      billing_info: {
        email: data.billing_info?.email || "",
      },
    });
    setIsLoading(false); // Set loading to false when data is fetched
  } catch (error) {
    setIsLoading(false); // Handle errors by stopping the loading state
  }
};

export const fetchStatisticsData = async (setInputValues, setIsLoading) => {
  try {
    const response = await fetch(URLs.PROFILE_STATISTICS_URL);
    if (!response.ok) {
      throw new Error("Network response was not ok");
    }
    const data = await response.json();

    setInputValues({
      last_login: data.last_login || "",
      user_joined: data.user_joined || "",
    });
    setIsLoading(false);
  } catch (error) {
    setIsLoading(false);
  }
};

export const reportsData = async (page = 1, setInputValues) => {
  try {
    const response = await fetch(URLs.REPORTS_URL + page);
    if (!response.ok) {
      throw new Error("Network response was not ok");
    }

    const data = await response.json();

    setInputValues({
      allocations: data?.allocations || [],
      current_page: data?.current_page || 1,
      total_pages: data?.total_pages || 1,
      producer_identity: {
        name: data?.static_data?.producer_identity?.name || "",
        url: data?.static_data?.producer_identity?.url || "",
      },
      strategy_name: data?.static_data?.strategy_name || "",
      press_release_pdf: data?.static_data?.press_release_pdf || "",
      validation_information: {
        strategy_commits:
          data?.static_data?.validation_information?.strategy_commits || 0,
        other_strategies_commits:
          data?.static_data?.validation_information?.other_strategies_commits ||
          0,
        total_commits:
          data?.static_data?.validation_information?.total_commits || 0,
      },
      available_for_download:
        data?.static_data?.available_for_download || false,
      download_ZIP: data?.static_data?.download_ZIP || "",
      download_single_CSV: data?.static_data?.download_single_CSV || "",
    });
  } catch (error) {
    console.log(error);
  }
};
export const reportsProducerData = async (page = 1, setInputValues) => {
  try {
    const response = await fetch(URLs.REPORTS_PRODUCER_URL + page);
    if (!response.ok) {
      throw new Error("Network response was not ok");
    }

    const data = await response.json();

    setInputValues({
      strategies: data?.strategies || [],
      current_page: data?.current_page || 1,
      total_pages: data?.total_pages || 1,
      producer_identity: {
        name: data?.static_data?.producer_identity?.name || "",
        verified_by: data?.static_data?.producer_identity?.verified_by || "",
      },
      press_release: data?.static_data?.press_release || "",
      validation_information: {
        total_strategies:
          data?.static_data?.validation_information?.total_strategies || 0,
        total_commits:
          data?.static_data?.validation_information?.total_commits || 0,
      },
    });
  } catch (error) {
    console.log(error);
  }
};

// This function fetches quota data from the server and checks if the quota banner should be shown
export const fetchQuotaData = async () => {
  try {
    const response = await fetch(URLs.PADDLE_DETAILS_URL);
    if (!response.ok) {
      throw new Error("Network response was not ok");
    }
    const data = await response.json();
    const quotaUsageInfo = data[0]?.subscription_plan?.total_quota_usage || 0;
    const userBalanceInfo = data[0]?.user_balance_info || {};
    const subscriptionInfo = data[0]?.subscription_info || {};
    const subscriptionPlan = data[0]?.subscription_plan || {};

    // Return the quota usage info and hide state
    return {
      quotaUsageInfo,
      userBalanceInfo,
      subscriptionPlan,
      subscriptionInfo,
    };
  } catch (error) {
    console.log(error);
    throw error; // In case the caller wants to handle the error
  }
};

export const fetchUserData = async (page = 1, setUserAddressHistoryData) => {
  try {
    const response = await fetch(
      URLs.USER_INFO_USER_ADDRESS_HISTORY_URL + "?page=" + page,
    );
    if (!response.ok) {
      throw new Error("Network response was not ok");
    }
    const data = await response.json();
    setUserAddressHistoryData(data);
  } catch (error) {
    console.log(error);
  }
};

//Create Name Collection
export const fetchCreateNameCollection = async (
  requestData,
  setNameCollectionError,
  setNameCollectionSuccess,
  setNameCollection,
  onShowConfirmationCollectionCreate,
  onUpdateStampQuota,
  handleLoading,
  close,
  form,
) => {
  try {
    handleLoading(true);
    const csrfToken = document.querySelector(
      "[name=csrfmiddlewaretoken]",
    ).value;

    const response = await fetch(URLs.PROFILE_COLLECTIONS_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRFToken": csrfToken,
      },
      body: JSON.stringify(requestData),
    });

    const result = await response.json();

    if (response.ok) {
      setNameCollectionError("");
      setNameCollection("");

      form.setFieldValue("collectionName", "");
      form.setFieldValue("collectionDescription", "");
      setNameCollectionSuccess(
        result.message +
          ": " +
          formatCollection(result.collection.name, result.collection.cid),
      );
      onUpdateStampQuota(Date.now());
      setTimeout(() => {
        setNameCollectionSuccess("");
      }, 5000);

      // Hide pop up
      close();
      // Hide Loading
      handleLoading(false);
    } else {
      setNameCollectionSuccess("");
      form.setFieldError("collectionName", result.error);

      // Hide pop up
      close();
      // Hide Loading
      handleLoading(false);
    }
  } catch (error) {
    setNameCollectionSuccess("");
    form.setFieldError("collectionName", error.message);

    // Hide pop up
    close();
    // Hide Loading
    handleLoading(false);
  }
};

//Set Name Collection
export const fetchSetNameCollection = async (
  requestData,
  setNameCollectionSuccess,
  onUpdateStampQuota,
  onOpenedModalSetName,
  setNameCollectionIsPinned,
  form,
  setLoading,
) => {
  try {
    setLoading(true);
    const csrfToken = document.querySelector(
      "[name=csrfmiddlewaretoken]",
    ).value;

    const response = await fetch(URLs.PROFILE_COLLECTIONS_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRFToken": csrfToken,
      },
      body: JSON.stringify(requestData),
    });

    const result = await response.json();

    if (response.ok) {
      form.setFieldValue("collectionName", "");
      onUpdateStampQuota(Date.now());
      setLoading(false);
      setNameCollectionIsPinned(response.url + requestData.cid);
      setNameCollectionSuccess("collection name added successfully");

      setTimeout(() => {
        setNameCollectionSuccess("");
      }, 2000);

      setTimeout(() => {
        onOpenedModalSetName(false);
      }, 4000);
      // Hide pop up
    } else {
      setNameCollectionSuccess("");
      form.setFieldError("collectionName", result.error);

      setLoading(false);
    }
  } catch (error) {
    setNameCollectionSuccess("");
    form.setFieldError("collectionName", error.message);

    setLoading(false);
  }
};

//Provider integration
// export const fetchProviderSubmit = async (requestData, form) => {
//   try {
//     const csrfToken = document.querySelector(
//       "[name=csrfmiddlewaretoken]",
//     ).value;
//
//     const response = await fetch(URLs.INTERACTIVE_BROKERS_AUTHORIZATION_URL + "?label=" + requestData.label, {
//       method: "GET",
//       headers: {
//         "Content-Type": "application/json",
//         "X-CSRFToken": csrfToken,
//       },
//
//     });
//
//      const result = await response.json();
//
//     if (response.ok) {
//       form.setFieldValue("providerNameInteractiveBroker", "");
//       console.log(result);
//     } else {
//       console.log("error");
//     }
//   } catch (error) {
//     form.setFieldError("providerNameInteractiveBroker", error.message);
//     console.log(error);
//   }
// };

// interactive brokers
export const getInteractiveBrokersProviders = async () => {
  try {
    const response = await fetch(URLs.PROVIDERS_URL);

    if (!response.ok) {
      throw new Error("Failed to fetch providers");
    }

    const data = await response.json();

    // filter only interactive brokers
    return data.filter((item) => item.provider_name === "interactivebrokers");
  } catch (error) {
    console.error("API Error:", error);
    return [];
  }
};

//All Collections is pinned
export const fetchAllCollectionsIsPinned = async (setInputValues) => {
  try {
    const csrfToken = document.querySelector(
      "[name=csrfmiddlewaretoken]",
    ).value;

    const response = await fetch(
      URLs.PROFILE_COLLECTIONS_URL + "?is_pinned=true&enable_pagination=false",
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "X-CSRFToken": csrfToken,
        },
      },
    );

    const result = await response.json();

    if (response.ok) {
      setInputValues({
        usercollections: result?.user_collections || [],
        current_page: result?.current_page || 1,
        total_pages: result?.total_pages || 1,
      });
    } else {
      console.log("Error:", result);
    }
  } catch (error) {
    console.log(error);
  }
};

//All Collections is pinned without pagination
export const fetchAllCollectionsIsPinnedWithoutPagination = async (
  handleSelectCollections,
) => {
  try {
    const csrfToken = document.querySelector(
      "[name=csrfmiddlewaretoken]",
    ).value;

    const response = await fetch(
      URLs.PROFILE_COLLECTIONS_URL + "?enable_pagination=false&is_pinned=true",
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "X-CSRFToken": csrfToken,
        },
      },
    );

    const result = await response.json();

    if (response.ok) {
      handleSelectCollections(result?.user_collections);
    } else {
      console.log("Error:", result);
    }
  } catch (error) {
    console.log(error);
  }
};

//All Collections is not pinned
export const fetchAllCollectionsIsNotPinned = async (
  setInputValuesIsNotPinned,
) => {
  try {
    const csrfToken = document.querySelector(
      "[name=csrfmiddlewaretoken]",
    ).value;

    const response = await fetch(
      URLs.PROFILE_COLLECTIONS_URL + "?is_pinned=false&enable_pagination=false",
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "X-CSRFToken": csrfToken,
        },
      },
    );

    const result = await response.json();

    if (response.ok) {
      setInputValuesIsNotPinned({
        usercollections: result?.user_collections || [],
        current_page: result?.current_page || 1,
        total_pages: result?.total_pages || 1,
      });
    } else {
      console.log("Error:", result);
    }
  } catch (error) {
    console.log(error);
  }
};

// Pin/Unpin Collection
export const fetchIsPinnedCollection = async (
  collection_id,
  isPinned,
  onOpenedModalPinned,
  setNameCollectionIsPinned,
) => {
  return updateCollectionProperty(
    collection_id,
    "is_pinned",
    isPinned,
    onOpenedModalPinned,
    setNameCollectionIsPinned,
  );
};

// Public/Private Collection
export const fetchIsPublicCollection = async (
  collection_id,
  isPublic,
  onOpenedModalPublic,
  setNameCollectionIsPinned,
) => {
  return updateCollectionProperty(
    collection_id,
    "is_public",
    isPublic,
    onOpenedModalPublic,
    setNameCollectionIsPinned,
    (response, value) => setNameCollectionIsPinned(value),
  );
};

// Enable/Disable Portfolio
// Common function for updating collection properties
const updateCollectionProperty = async (
  collection_id,
  property,
  value,
  onOpenedModal,
  setChange,
  customSuccessHandler = null,
) => {
  try {
    const csrfToken = document.querySelector(
      "[name=csrfmiddlewaretoken]",
    ).value;

    const response = await fetch(
      URLs.PROFILE_COLLECTIONS_URL + collection_id + "/",
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          "X-CSRFToken": csrfToken,
        },
        body: JSON.stringify({ [property]: value }),
      },
    );
    if (response.ok) {
      onOpenedModal(false);
      if (customSuccessHandler) {
        customSuccessHandler(response, value);
      } else {
        setChange(response.url + value);
      }
    }
  } catch (error) {
    console.log(error);
    onOpenedModal(false);
  }
};

export const fetchPortfolioCollection = async (
  collection_id,
  portfolio,
  onOpenedModalPortfolio,
  setPortfolioChange,
) => {
  return updateCollectionProperty(
    collection_id,
    "is_portfolio",
    portfolio,
    onOpenedModalPortfolio,
    setPortfolioChange,
  );
};

// Enable/Disable Portfolio Collection
export const fetchPortfolioCollectionType = async (
  collection_id,
  portfolio_collection,
  onOpenedModalPortfolioCollection,
  setPortfolioCollectionChange,
) => {
  return updateCollectionProperty(
    collection_id,
    "is_portfolio_collection",
    portfolio_collection,
    onOpenedModalPortfolioCollection,
    setPortfolioCollectionChange,
  );
};

// Add/Edit Description Collection
export const fetchPutDescriptionCollection = async (
  collection_id,
  description,
  onOpenedModalDescription,
  onDescription,
  form,
  setDescriptionCreateSuccess,
) => {
  try {
    const csrfToken = document.querySelector(
      "[name=csrfmiddlewaretoken]",
    ).value;

    const response = await fetch(
      URLs.PROFILE_COLLECTIONS_URL + collection_id + "/",
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          "X-CSRFToken": csrfToken,
        },
        body: JSON.stringify({ description: description }),
      },
    );
    if (response.ok) {
      onDescription(response.url + description);

      setDescriptionCreateSuccess(true);
      setTimeout(() => {
        setDescriptionCreateSuccess(false);
        onOpenedModalDescription(false);
      }, 2000);
      return true;
    } else {
      form.setFieldError("collectionDescription", response.error);
      return false;
    }
  } catch (error) {
    form.setFieldError("collectionDescription", error);
    return false;
  }
};

export const footerForm = async (values, setSuccess, form) => {
  const csrfToken = document.querySelector("[name=csrfmiddlewaretoken]").value;
  const email = values.email;
  const formData = { email };

  try {
    const response = await fetch(URLs.CONTACT_APP_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRFToken": csrfToken,
      },
      body: JSON.stringify(formData),
    });

    const data = await response.json();

    if (!response.ok) {
      form.setFieldError("email", data.error);
      throw new Error(data.error || "Something went wrong");
    }
    form.setFieldValue("email", "");
    setSuccess("Thank You!");
  } catch (error) {
    form.setFieldError("email", "Error");
  }
};

// IB
export const fetchInteractiveBrokersAccount = async (
  providerName,
  setAccountInfo,
  setLoaderAccountInfo,
  setLoaderButtons,
  ignore_access_token = false,
) => {
  let urlBroker;
  if (ignore_access_token) {
    urlBroker = `${URLs.INTERACTIVE_BROKERS_AUTHORIZATION_ACCOUNTS_URL}?label=${providerName}&ignore_access_token=true`;
  } else {
    urlBroker = `${URLs.INTERACTIVE_BROKERS_AUTHORIZATION_ACCOUNTS_URL}?label=${providerName}`;
  }
  try {
    const response = await fetch(urlBroker, {
      method: "GET",
      headers: {
        Accept: "application/json",
      },
    });

    const data = await response.json();

    if (data.redirect_url) {
      window.location.href = data.redirect_url;
    } else if (data.account) {
      setAccountInfo(data.account[0]);

      setTimeout(() => {
        setLoaderAccountInfo(false);
        setLoaderButtons((prev) => ({ ...prev, [providerName]: false }));
      }, 3000);
    } else {
      setLoaderButtons((prev) => ({ ...prev, [providerName]: false }));
      setLoaderAccountInfo(false);
    }
  } catch (error) {
    setLoaderButtons((prev) => ({ ...prev, [providerName]: false }));
    setLoaderAccountInfo(false);
  }
};

// IB delete
export const deleteProviderRequest = async (
  providerName,
  { onSuccess = () => {}, onFinally = () => {} } = {},
) => {
  const csrfToken = document.querySelector("[name=csrfmiddlewaretoken]").value;
  try {
    const response = await fetch(
      `${URLs.INTERACTIVE_BROKERS_AUTHORIZATION_ACCOUNTS_URL}?label=${providerName}`,
      {
        method: "DELETE",
        headers: {
          Accept: "application/json",
          "X-CSRFToken": csrfToken,
        },
      },
    );

    const data = await response.json();

    if (data.message) {
      notifications.show({
        title: "Success",
        message: data.message,
        color: "green",
      });
      onSuccess();
    } else {
      notifications.show({
        title: "Error",
        message: data.error,
        color: "red",
      });
    }
  } catch (error) {
    notifications.show({
      title: "Error",
      message: error.message || "An unknown error occurred",
      color: "red",
    });
  } finally {
    onFinally();
  }
};

export const createVerifiedFileDownload = async (
  fileData,
  setLoadingCreateLink,
) => {
  if (!fileData || typeof fileData !== "object") {
    setLoadingCreateLink(false);
    return { data: { error: "Invalid fileData" } };
  }

  if (
    !fileData.name ||
    !fileData.metadata ||
    !fileData.metadata.object_cid ||
    !fileData.metadata.timestamp ||
    !fileData.metadata.user ||
    !fileData.collectionName
  ) {
    setLoadingCreateLink(false);
    return { data: { error: "Missing required fields in fileData" } };
  }

  const requestData = {
    file_name: fileData.name,
    object_cid: fileData.metadata.object_cid,
    collection_cid: sha3_256_hash_bytes(fileData.collectionName),
    commitment_receipt_timestamp: fileData.metadata.timestamp,
    address: fileData.metadata.user,
  };

  const csrfInput = document.querySelector("[name=csrfmiddlewaretoken]");
  if (!csrfInput || !csrfInput.value) {
    setLoadingCreateLink(false);
    return { data: { error: "CSRF token not found in DOM" } };
  }

  const csrfToken = csrfInput.value;
  setLoadingCreateLink(true);

  try {
    const response = await fetch(URLs.CREATE_VERIFIED_FILE_DOWNLOAD, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRFToken": csrfToken,
      },
      body: JSON.stringify(requestData),
    });

    const data = await response.json();

    if (!data || typeof data !== "object") {
      setLoadingCreateLink(false);
      return { data: { error: "Invalid response data" } };
    }

    if (response.ok) {
      if (data.success) {
        setLoadingCreateLink(false);
        return { data: data };
      } else {
        setLoadingCreateLink(false);
        return { data: { error: data.error || "Unknown server error" } };
      }
    } else {
      setLoadingCreateLink(false);
      return {
        data: {
          error: data.error || `Request failed with status ${response.status}`,
        },
      };
    }
  } catch (error) {
    setLoadingCreateLink(false);
    return { data: { error: error.message || "Network error" } };
  }
};

export const getVerifiedFileByHash = async (fileHash) => {
  if (!fileHash) {
    return { data: { error: "Missing required field fileHash" } };
  }

  const requestData = {
    filehash: fileHash,
  };

  const csrfInput = document.querySelector("[name=csrfmiddlewaretoken]");
  if (!csrfInput || !csrfInput.value) {
    return { data: { error: "CSRF token not found in DOM" } };
  }

  const csrfToken = csrfInput.value;

  try {
    const response = await fetch(URLs.VERIFIED_FILE_HASH, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRFToken": csrfToken,
      },
      body: JSON.stringify(requestData),
    });

    const data = await response.json();
    if (!data || typeof data !== "object") {
      return { data: { error: "Invalid response data" } };
    }

    if (response.ok) {
      return { data: data };
    } else {
      return {
        data: {
          error: data.error || `Request failed with status ${response.status}`,
        },
      };
    }
  } catch (error) {
    return { data: { error: error.message || "Network error" } };
  }
};

export const getVerifiedFileDownloadUrl = async (linkId) => {
  if (!linkId) {
    return { error: "Missing required field linkId" };
  }

  const requestData = { "link-id": linkId };

  const csrfInput = document.querySelector("[name=csrfmiddlewaretoken]");
  if (!csrfInput || !csrfInput.value) {
    return { error: "CSRF token not found in DOM" };
  }

  const csrfToken = csrfInput.value;

  try {
    const response = await fetch(URLs.DOWNLOAD_VERIFIED_FILE, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRFToken": csrfToken,
      },
      body: JSON.stringify(requestData),
    });

    const data = await response.json();

    if (!data || typeof data !== "object") {
      return { error: "Invalid response data" };
    }

    if (response.ok) {
      return { url: data.url };
    } else {
      return {
        error: data.error || `Request failed with status ${response.status}`,
      };
    }
  } catch (error) {
    return { error: error.message || "Network error" };
  }
};

// Helper function to get original file info for PDF files
const getOriginalFileInfo = async (linkId) => {
  const csrfInput = document.querySelector("[name=csrfmiddlewaretoken]");
  if (!csrfInput?.value) return null;

  try {
    const response = await fetch(URLs.DOWNLOAD_VERIFIED_FILE, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRFToken": csrfInput.value,
      },
      body: JSON.stringify({ "link-id": linkId }),
    });

    if (!response.ok) return null;

    const { url: fileUrl } = await response.json();

    // Get file info from the original file URL
    const fileResponse = await fetch(fileUrl, {
      method: "GET",
      mode: "cors",
      credentials: "omit",
    });

    const contentLength = fileResponse.headers.get("Content-Length");
    const filename = new URL(fileUrl).pathname
      .split("/")
      .pop()
      .split("?")[0]
      .toLowerCase();

    const fileExtension = filename.includes(".")
      ? filename.split(".").pop()
      : "";

    const fileSize = contentLength ? parseInt(contentLength, 10) || 0 : 0;

    return { extension: fileExtension, size: fileSize };
  } catch (error) {
    console.error("Error getting original file info:", error);
    return null;
  }
};

export const getFilePreview = async (linkId, setFileSize, setFileExtension) => {
  if (!linkId) {
    return { error: "Missing required field linkId" };
  }

  const csrfInput = document.querySelector("[name=csrfmiddlewaretoken]");
  if (!csrfInput?.value) {
    return { error: "CSRF token not found in DOM" };
  }

  try {
    const response = await fetch(URLs.PREVIEW_VERIFIED_FILE, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRFToken": csrfInput.value,
      },
      body: JSON.stringify({ "link-id": linkId }),
    });

    if (!response.ok) {
      const data = await response.json();
      return {
        error: data?.error || `Request failed with status ${response.status}`,
      };
    }

    const { url: fileUrl } = await response.json();
    if (!fileUrl) {
      return { error: "Invalid response data" };
    }

    // Get file type and size
    const getFileTypeAndSize = async (url) => {
      // Check if this is a PDF file by getting original file info
      const originalFileInfo = await getOriginalFileInfo(linkId);

      if (originalFileInfo?.extension === "pdf") {
        setFileExtension(originalFileInfo.extension);
        setFileSize(originalFileInfo.size);
        return { fileType: "pdf", fileSize: originalFileInfo.size };
      }

      // For non-PDF files, get info from the preview URL
      const fileResponse = await fetch(url, {
        method: "GET",
        mode: "cors",
        credentials: "omit",
      });

      const contentLength = fileResponse.headers.get("Content-Length");
      const filename = new URL(url).pathname
        .split("/")
        .pop()
        .split("?")[0]
        .toLowerCase();

      const extensionMap = {
        text: [".txt"],
        image: [".png", ".jpg", ".jpeg", ".gif"],
        csv: [".csv"],
        html: [".html", ".htm"],
      };

      const fileType =
        Object.entries(extensionMap).find(([, extensions]) =>
          extensions.some((ext) => filename.endsWith(ext)),
        )?.[0] || "unknown";

      const fileSize = contentLength ? parseInt(contentLength, 10) || 0 : 0;
      const fileExtension = filename.includes(".")
        ? filename.split(".").pop()
        : "";

      setFileExtension(fileExtension);
      setFileSize(fileSize);
      return { fileType, fileSize };
    };

    const { fileType, fileSize } = await getFileTypeAndSize(fileUrl);

    // Generate preview based on file type
    const generatePreview = async (url, type) => {
      switch (type) {
        case "text":
        case "csv": {
          const response = await fetch(url);
          return await response.text();
        }
        case "html": {
          const response = await fetch(url);
          const rawHtml = await response.text();

          const allowedTags = [
            "p",
            "strong",
            "em",
            "u",
            "s",
            "code",
            "h1",
            "h2",
            "h3",
            "h4",
            "h5",
            "h6",
            "blockquote",
            "ul",
            "ol",
            "li",
            "a",
            "br",
          ];
          const allowedAttributes = ["href", "title", "rel", "target"];
          const allowedSchemes = ["http", "https", "mailto", "tel"];

          // Sanitize href attributes
          DOMPurify.addHook("uponSanitizeAttribute", (node, data) => {
            if (data.attrName === "href") {
              try {
                const url = new URL(data.attrValue, window.location.origin);
                if (!allowedSchemes.includes(url.protocol.replace(":", ""))) {
                  data.attrValue = "#";
                }
              } catch {
                data.attrValue = "#";
              }
              // Prevent dangerous schemes
              if (/^\s*(javascript|data):/i.test(data.attrValue)) {
                data.attrValue = "#";
              }
            }
          });

          const sanitized = DOMPurify.sanitize(rawHtml, {
            ALLOWED_TAGS: allowedTags,
            ALLOWED_ATTR: allowedAttributes,
            FORBID_SCRIPT: true,
            FORBID_TAGS: ["script", "object", "embed", "base", "link"],
          });

          DOMPurify.removeHook("uponSanitizeAttribute");
          return sanitized;
        }
        case "image":
        case "pdf":
          return url;
        default:
          return "Preview unavailable for this file type.";
      }
    };

    // Validate file size and type
    if (fileSize > FILE_SIZE_PREVIEW_LIMIT_BYTES) {
      return { error: FILE_SIZE_PREVIEW_TOO_LARGE_MESSAGE };
    }

    if (fileType === "unknown") {
      return { error: "Preview unavailable for this file type." };
    }

    // Generate and return preview
    const preview = await generatePreview(fileUrl, fileType);
    return { preview, fileType };
  } catch (error) {
    console.error("Error generating preview:", error);
    return { error: error.message || "Network error" };
  }
};

// Get all notifications
export const fetchNotifications = async (setNotifications) => {
  try {
    const response = await fetch(URLs.NOTIFICATIONS_URL, {
      method: "GET",
      headers: {
        Accept: "application/json",
      },
    });

    if (!response.ok) {
      // Check for HTTP errors (4xx or 5xx status codes)
      console.error(`HTTP error! status: ${response.status}`);
      return;
    }

    const data = await response.json();

    if (data && data.notifications) {
      // Check if data is an array (or whatever structure you expect)
      if (!Array.isArray(data.notifications)) {
        console.error("Data is not an array:", data.notifications);
        return; // Or throw error
      }

      setNotifications(data.notifications);
    } else {
      console.warn("No data received from the API.");
      setNotifications([]);
    }
  } catch (error) {
    console.error("Error fetching notifications:", error);
  }
};

// Authentication check cache
let authCheckCache = {
  result: null,
  timestamp: 0,
  promise: null,
};

const AUTH_CHECK_CACHE_DURATION = 3000; // 3 seconds cache

/**
 * Check if user is authenticated.
 * Uses caching to avoid multiple simultaneous requests.
 *
 * @param {Function} setIsAuthenticated - Optional callback to update authentication state
 * @param {boolean} forceRefresh - Force refresh cache (default: false)
 * @returns {Promise<boolean>} Promise that resolves to authentication status
 */
export const checkAuthentication = async (
  setIsAuthenticated = null,
  forceRefresh = false,
) => {
  const now = Date.now();
  const cacheValid = now - authCheckCache.timestamp < AUTH_CHECK_CACHE_DURATION;

  // Return cached result if valid and not forcing refresh
  if (cacheValid && authCheckCache.result !== null && !forceRefresh) {
    if (setIsAuthenticated) {
      setIsAuthenticated(authCheckCache.result);
    }
    return authCheckCache.result;
  }

  // If there's already a pending request, wait for it
  if (authCheckCache.promise) {
    try {
      const result = await authCheckCache.promise;
      if (setIsAuthenticated) {
        setIsAuthenticated(result);
      }
      return result;
    } catch (error) {
      // If pending request fails, continue with new request
      console.warn("Pending auth check failed:", error);
    }
  }

  // Create new request
  authCheckCache.promise = (async () => {
    try {
      const response = await fetch(URLs.CHECK_AUTHENTICATION_URL, {
        method: "GET",
        credentials: "same-origin",
      });

      if (response.ok) {
        const authData = await response.json();
        const isAuthenticated = authData.is_authenticated === true;

        // Update cache
        authCheckCache.result = isAuthenticated;
        authCheckCache.timestamp = Date.now();
        authCheckCache.promise = null;

        // Update state if callback provided
        if (setIsAuthenticated) {
          setIsAuthenticated(isAuthenticated);
        }

        return isAuthenticated;
      } else {
        // If response is not ok, assume not authenticated
        authCheckCache.result = false;
        authCheckCache.timestamp = Date.now();
        authCheckCache.promise = null;

        if (setIsAuthenticated) {
          setIsAuthenticated(false);
        }

        return false;
      }
    } catch (error) {
      // On error, clear promise and return null (unknown state)
      authCheckCache.promise = null;
      console.warn("Failed to check authentication:", error);
      return null;
    }
  })();

  try {
    return await authCheckCache.promise;
  } catch (error) {
    return null;
  }
};

// S3 Link Validation
export const validateS3Link = async (
  s3Link,
  setLoading,
  onSuccess,
  onError,
) => {
  if (!s3Link || typeof s3Link !== "string" || s3Link.trim() === "") {
    onError("S3 link is required");
    return;
  }

  try {
    setLoading(true);

    const csrfToken = document.querySelector(
      "[name=csrfmiddlewaretoken]",
    ).value;

    const requestData = {
      s3_link: s3Link.trim(),
    };

    const response = await fetch(URLs.S3_LINK_VALIDATION_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRFToken": csrfToken,
      },
      body: JSON.stringify(requestData),
    });

    const data = await response.json();

    if (response.ok) {
      if (data.success) {
        onSuccess(data);
      } else {
        onError(data.error || "Validation failed");
      }
    } else {
      onError(data.error || `Request failed with status ${response.status}`);
    }
  } catch (error) {
    console.error("S3 Link Validation Error:", error);
    onError(error.message || "Network error occurred");
  } finally {
    setLoading(false);
  }
};

// S3 Credentials Validation
export const validateS3Credentials = async (
  requestData,
  setLoading,
  onSuccess,
  onError,
) => {
  try {
    setLoading(true);

    const csrfToken = document.querySelector(
      "[name=csrfmiddlewaretoken]",
    ).value;

    // Add sync flag
    const payload = {
      ...requestData,
      sync: false,
    };

    const response = await fetch(URLs.S3_CREDENTIALS_VALIDATION_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRFToken": csrfToken,
      },
      body: JSON.stringify(payload),
    });

    if (response.ok) {
      try {
        const data = await response.json();
        // Backend always returns job_id and status
        if (data.job_id) {
          onSuccess({ job_id: data.job_id, status: data.status });
        } else {
          onError("Unexpected response format: missing job_id");
        }
      } catch (error) {
        onError("Failed to parse JSON response");
      }
    } else {
      // Try to parse error as JSON
      try {
        const errorData = await response.json();
        // Pass both error message and full error data to callback
        onError(
          errorData.error || `Request failed with status ${response.status}`,
          errorData,
        );
      } catch {
        onError(`Request failed with status ${response.status}`);
      }
    }
  } catch (error) {
    console.error("S3 Credentials Validation Error:", error);
    onError(error.message || "Network error occurred");
  } finally {
    setLoading(false);
  }
};

// Check S3 Validation Status (for long-running tasks)
export const checkS3ValidationStatus = async (
  jobId,
  setLoading,
  onSuccess,
  onError,
  onProgress,
) => {
  if (!jobId) {
    onError("Job ID is required");
    return;
  }

  try {
    // Don't set loading for polling requests
    if (setLoading) {
      setLoading(true);
    }

    const csrfToken = document.querySelector(
      "[name=csrfmiddlewaretoken]",
    ).value;

    const response = await fetch(`${URLs.S3_VALIDATION_STATUS_URL}${jobId}/`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        "X-CSRFToken": csrfToken,
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      onError(
        errorData.error || `Request failed with status ${response.status}`,
        errorData,
      );
      if (setLoading) {
        setLoading(false);
      }
      return;
    }

    const data = await response.json();

    const status = data.status;
    const state = data.state || {};
    const progress = state.progress || {};
    const extra = state.extra || {};

    if (status === "ready") {
      // Check if there's an error even though status is "ready"
      if (data.error) {
        // Treat as failed if there's an error
        const errorMessage = data.error || "Validation failed";
        onError(errorMessage, data);
        if (setLoading) {
          setLoading(false);
        }
      } else {
        // Job completed successfully - data contains verification results
        if (onSuccess) {
          onSuccess(data);
        }
        if (setLoading) {
          setLoading(false);
        }
      }
    } else if (status === "failed") {
      // Job failed - pass full data object to error handler for config extraction
      const errorMessage = data.error || extra.exception || "Validation failed";
      onError(errorMessage, data);
      if (setLoading) {
        setLoading(false);
      }
    } else if (
      status === "pending" ||
      status === "loading" ||
      status === "processing"
    ) {
      // Job in progress - call progress callback with real status and progress from backend

      const processedFiles =
        data.processed_files !== undefined
          ? data.processed_files
          : progress.processed_files || 0;
      const totalFiles =
        data.total_files !== undefined
          ? data.total_files
          : progress.total_files || 0;

      if (onProgress) {
        onProgress({
          status: status,
          total_files: totalFiles,
          processed_files: processedFiles,
          progress_ratio: progress.progress_ratio || 0,
          last_processed_file: extra.last_processed_file || null,
          config: data.config || null,
        });
      }

      if (setLoading) {
        setLoading(false);
      }
    } else {
      // Unknown status
      onError(`Unknown job status: ${status}`, data);
      if (setLoading) {
        setLoading(false);
      }
    }
  } catch (error) {
    console.error("S3 Validation Status Check Error:", error);
    onError(error.message || "Network error occurred", {});
    if (setLoading) {
      setLoading(false);
    }
  }
};
